import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Slider,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Rating,
  Button,
  IconButton,
  Collapse,
  Chip,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import FilterListIcon from '@mui/icons-material/FilterList';

interface ProductFiltersProps {
  categories: Array<{ id: string; name: string; icon?: React.ReactNode; count: number }>;
  priceRange: number[];
  setPriceRange: (range: number[]) => void;
  selectedCategories: string[];
  setSelectedCategories: (categories: string[]) => void;
  minRating: number | null;
  setMinRating: (rating: number | null) => void;
  onClear: () => void;
}

const ProductFilters: React.FC<ProductFiltersProps> = ({
  categories,
  priceRange,
  setPriceRange,
  selectedCategories,
  setSelectedCategories,
  minRating,
  setMinRating,
  onClear,
}) => {
  const [collapsed, setCollapsed] = React.useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const appliedCount = selectedCategories.length + (minRating ? 1 : 0);

  const handleCategoryToggle = (categoryId: string) => {
    const currentIndex = selectedCategories.indexOf(categoryId);
    const newChecked = [...selectedCategories];

    if (currentIndex === -1) {
      newChecked.push(categoryId);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setSelectedCategories(newChecked);
  };

  return (
    <Paper elevation={0} sx={{ border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : '#eee'}`, height: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon fontSize="small" color="action" />
          <Typography variant="h6" fontWeight="bold">
            Filters
          </Typography>
          {appliedCount > 0 && <Chip label={`${appliedCount}`} size="small" color="primary" />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!collapsed && (
            <Button size="small" onClick={onClear} color="secondary" sx={{ textTransform: 'none' }}>
              Clear
            </Button>
          )}
          <IconButton size="small" onClick={() => setCollapsed((prev) => !prev)}>
            <ChevronLeftIcon
              sx={{
                transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
              }}
            />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={!collapsed} timeout={200} unmountOnExit>
        <Box sx={{ px: 2, pb: 2 }}>
          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            Categories
          </Typography>
          <FormGroup sx={{ mb: 3 }}>
            {categories.map((cat) => (
              <FormControlLabel
                key={cat.id}
                control={
                  <Checkbox
                    checked={selectedCategories.includes(cat.id)}
                    onChange={() => handleCategoryToggle(cat.id)}
                    size="small"
                    color="primary"
                    disabled={cat.count === 0}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{cat.icon}</span>
                    <Typography variant="body2">{cat.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({cat.count})
                    </Typography>
                  </Box>
                }
              />
            ))}
          </FormGroup>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            Price Range (KSh)
          </Typography>
          <Box sx={{ px: 1, mb: 3 }}>
            <Slider
              value={priceRange}
              onChange={(_, newValue) => setPriceRange(newValue as number[])}
              valueLabelDisplay="auto"
              min={0}
              max={20000}
              step={500}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption">KSh {priceRange[0]}</Typography>
              <Typography variant="caption">KSh {priceRange[1]}+</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            Rating
          </Typography>
          <List dense sx={{ mb: 2 }}>
            {[4, 3, 2, 1].map((rating) => (
              <ListItemButton
                key={rating}
                onClick={() => setMinRating(rating === minRating ? null : rating)}
                sx={{
                  borderRadius: 1,
                  bgcolor: minRating === rating ? 'action.selected' : 'transparent',
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox edge="start" checked={minRating === rating} tabIndex={-1} disableRipple size="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Rating value={rating} readOnly size="small" />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        & Up
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ProductFilters;
